import React, { useState, useEffect } from "react";
import { X, Smartphone, Share, MoreVertical, PlusSquare, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

// Detect OS
function getOS() {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  if (/Android/.test(ua)) return "android";
  return "other";
}

// Detect if already installed as PWA
function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function PWAInstallPrompt({ onClose }) {
  const [os] = useState(() => getOS());
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [tab, setTab] = useState(os === "ios" ? "ios" : "android");

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleNativeInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setInstalled(true);
      setTimeout(onClose, 2000);
    }
    setDeferredPrompt(null);
  };

  if (isStandalone()) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white rounded-t-3xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 1rem)" }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1.5 rounded-full bg-gray-200" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between px-5 pt-3 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-[#7B2D3F] flex items-center justify-center shadow-lg shadow-[#7B2D3F]/30">
              <span className="text-white font-black text-base tracking-widest">HS</span>
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900">Ho-Sys</h2>
              <p className="text-sm text-gray-400">Fitxatge i gestió del torn</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Installed confirmation */}
        {installed ? (
          <div className="px-5 pb-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-500" />
            </div>
            <p className="text-xl font-bold text-gray-900">Instal·lat!</p>
            <p className="text-gray-400 text-sm mt-1">Ara pots fitxar des de la icona del teu mòbil.</p>
          </div>
        ) : (
          <>
            {/* Benefits */}
            <div className="px-5 mb-4">
              <div className="grid grid-cols-3 gap-2 text-center">
                {[
                  { icon: "⚡", text: "Accés instantani" },
                  { icon: "📍", text: "Sense navegador" },
                  { icon: "🔒", text: "Sessió guardada" },
                ].map(({ icon, text }) => (
                  <div key={text} className="bg-[#f8f3f4] rounded-xl py-3 px-2">
                    <p className="text-2xl mb-1">{icon}</p>
                    <p className="text-[11px] font-semibold text-gray-600">{text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Native install (Android Chrome) */}
            {deferredPrompt && tab === "android" && (
              <div className="px-5 pb-5">
                <Button
                  onClick={handleNativeInstall}
                  className="w-full bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white rounded-2xl h-14 text-base font-bold shadow-lg shadow-[#7B2D3F]/30"
                >
                  <Smartphone className="w-5 h-5 mr-2" />
                  Instal·lar Ho-Sys
                </Button>
                <p className="text-xs text-gray-400 text-center mt-2">S'afegirà a la pantalla d'inici automàticament</p>
              </div>
            )}

            {/* Manual instructions */}
            {(!deferredPrompt || os === "ios") && (
              <>
                {/* OS tabs */}
                <div className="flex gap-1 px-5 mb-4">
                  <button
                    onClick={() => setTab("android")}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${tab === "android" ? "bg-[#7B2D3F] text-white" : "bg-gray-100 text-gray-500"}`}
                  >
                    Android
                  </button>
                  <button
                    onClick={() => setTab("ios")}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${tab === "ios" ? "bg-[#7B2D3F] text-white" : "bg-gray-100 text-gray-500"}`}
                  >
                    iPhone / iPad
                  </button>
                </div>

                {tab === "android" && (
                  <div className="px-5 pb-6 space-y-3">
                    {[
                      { icon: MoreVertical, text: 'Toca els tres punts (⋮) a la barra del navegador' },
                      { icon: PlusSquare, text: 'Selecciona "Afegir a la pantalla d\'inici"' },
                      { icon: CheckCircle2, text: 'Confirma i toca "Afegir"' },
                    ].map(({ icon: Icon, text }, i) => (
                      <div key={`item-${i}`} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                        <div className="w-8 h-8 bg-[#7B2D3F] rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-xs">{i + 1}</span>
                        </div>
                        <p className="text-sm text-gray-700 flex-1">{text}</p>
                        <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </div>
                    ))}
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700 text-center">
                      Utilitza Chrome o Edge per a una millor experiència
                    </div>
                  </div>
                )}

                {tab === "ios" && (
                  <div className="px-5 pb-6 space-y-3">
                    {[
                      { icon: Share, text: 'Toca la icona de compartir (⬆) a la barra inferior de Safari' },
                      { icon: PlusSquare, text: 'Desplaça\'t cap avall i toca "Afegir a la pantalla d\'inici"' },
                      { icon: CheckCircle2, text: 'Escriu "Ho-Sys" i toca "Afegir"' },
                    ].map(({ icon: Icon, text }, i) => (
                      <div key={`item-${i}`} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                        <div className="w-8 h-8 bg-[#7B2D3F] rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-xs">{i + 1}</span>
                        </div>
                        <p className="text-sm text-gray-700 flex-1">{text}</p>
                        <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </div>
                    ))}
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-xs text-amber-700 text-center">
                      Utilitza Safari (no Chrome) per instal·lar al iPhone
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="px-5 pb-4">
              <button onClick={onClose} className="w-full text-gray-400 text-sm py-2 hover:text-gray-600 transition-colors">
                Ara no, gràcies
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}